<template>
    <div>
        <!-- Impersonation Banner -->
        <div v-if="profile && isImpersonating" class="bg-secondary-500 text-black text-center p-2">
          <p>You are currently impersonating {{ profile.fullName }}.</p>
          <button @click="stopImpersonation" class="btn mt-4 btn-primary btn-md font-bold">Stop Impersonation</button>
        </div>
      
        <!-- Desktop Nav -->
        <nav class="lg:flex items-center justify-between md:px-8 px-4 hidden" style="background-color: #122539;" aria-label="Desktop Navigation">
            <div class="flex items-center text-white flex-grow">
                <div class="flex py-4 items-center flex-shrink">
                    <a href="/" class="flex items-center">
                        <img :src="orgSettings.logoUrl" :alt="orgSettings.name" style="height:50px" />
                        <p class="pl-4 text-2xl" v-if="orgSettings.showNameInNav">{{ orgSettings.applicationDisplayName }}</p>
                    </a>
                </div>

                <div class="flex pl-5">
                    <a href="/programs" class="font-bold text-lg text-white lg:pt-0 pt-4 px-5">All Programs</a>
                </div>

                <!-- Search Bar -->
                <div class="flex-grow pl-5">
                    <input v-model="searchQuery"
                           @input="debouncedSearch"
                           @keydown.esc="closeSearch"
                           class="bg-gray-200 rounded-full px-4 py-2 text-black"
                           style="width:300px"
                           placeholder="Search for anything" />
                </div>
            </div>

            <div v-if="loggedIn && profile && profile.fullName && !hasRole('Anonymous')" class="lg:w-4/12 block w-full lg:flex lg:items-center lg:pt-0 pt-2 lg:justify-end" :class="{ hidden: !expanded }">
                <div class="flex w-full lg:justify-end">
                    <d-dropdown aria-role="list">
                        <template #activator>
                            <div class="flex items-center">
                                <span class="text-lg text-white mr-3">{{ profile.fullName }}</span>
                                <d-avatar color="orange" :text="profile.fullName" :img-src="profile.profileImageUrl" class="my-0" size="30px" />
                            </div>
                        </template>
                        <li><a href="/profile" class="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-150" role="menuitem">View Profile</a></li>
                        <li v-if="orgSettings.studentGradeCheckEnabled"><a href="/grades" class="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-150" role="menuitem">Grades</a></li>
                        <li v-if="profile && hasRole('Super Admin')"><a @click.prevent="openImpersonateModal" class="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-150 cursor-pointer" role="menuitem">Impersonate User</a></li>
                        <li><a href="/account/logout" class="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-150" role="menuitem">Logout</a></li>
                    </d-dropdown>
                </div>
            </div>
            <div v-else-if="!loggedIn || !profile || !profile.fullName || hasRole('Anonymous')" class="flex items-center space-x-4">
                <div v-if="!orgSettings.accountsDisabled" class="flex items-center space-x-4">
                    <a href="/promo" class="font-bold text-lg text-white hover:text-secondary-500 transition-colors">Redeem Promo</a>
                    <a :href="loginUrl" class="font-bold text-lg text-white hover:text-secondary-500 transition-colors">Login</a>
                    <a href="/register" class="font-bold text-lg text-white hover:text-secondary-500 transition-colors">Create Account</a>
                </div>
            </div>
        </nav>

        <!-- Search Results -->
        <div v-if="searchResults.length > 0 || searchQuery.length >= 3" class="search-results p-4">
            <button class="close-btn" @click="closeSearch">X</button>
            <h2 class="text-xl font-bold text-black mb-4">Search Results</h2>
            <div v-if="searchResults.length === 0" class="no-results">
                <p class="text-gray-800">No results found for "{{ searchQuery }}". Please try a different search term.</p>
            </div>
            <div v-for="(results, type) in groupedResults" :key="type" class="mb-4">
                <h3 class="font-bold text-lg text-black mb-2">{{ type }}</h3>
                <ul>
                    <li v-for="(result, index) in results"
                        :key="result.id"
                        :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-400'"
                        class="flex justify-between items-center p-2 w-full border-t">
                        <div class="result-info w-full flex justify-between items-center">
                            <div class="flex-1 pr-4">
                                <a :href="result.url" class="text-black font-bold block">{{ result.title }}</a>
                                <p class="text-black">{{ truncateDescription(result.description) }}</p>
                            </div>
                            <button @click="navigateTo(result.url)" class="btn btn-primary">Go</button>
                        </div>
                    </li>
                </ul>
            </div>
        </div>

        <!-- Mobile Nav -->
        <nav class="flex items-center justify-between flex-wrap md:px-8 px-4 lg:hidden" style="background-color: #122539;" aria-label="Mobile Navigation">
            <div class="flex items-center flex-grow text-white mr-6">
                <div class="justify-start flex w-full flex-no-wrap py-4 items-center">
                    <a href="/" class="flex items-center">
                        <img :src="orgSettings.logoUrl" alt="orgSettings.name" style="height:50px" />
                        <p class="pl-4 text-2xl" v-if="orgSettings.showNameInNav">{{ orgSettings.applicationDisplayName }}</p>
                    </a>
                </div>
            </div>

            <!-- Search Bar -->
            <div class="flex-grow pl-5 pb-3">
                <input v-model="searchQuery"
                       @input="debouncedSearch"
                       @keydown.esc="closeSearch"
                       class="bg-gray-200 rounded-full px-4 py-2 text-black"
                       placeholder="Search for anything" />
            </div>

            <div class="flex justify-end">
                <button class="flex items-center justify-end px-3 py-2 border rounded text-white border-white hover:text-primary-200 hover:border-primary-200" @click="expanded = !expanded">
                    <svg class="fill-current h-3 w-3" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <title>Menu</title>
                        <path d="M0 3h20v2H0V3zm0 6h20v2H0V9zm0 6h20v2H0v-2z" />
                    </svg>
                </button>
            </div>
            <div class="w-full flex flex-wrap pb-5" :class="{ hidden: !expanded }">
                <!-- Same as the desktop "All Programs" link; the logo goes to "/", which can be the splash page -->
                <a href="/programs" class="font-bold text-lg text-white pt-4 px-2 w-full">All Programs</a>
                <a v-if="loggedIn && profile && !profile.contentListingPromoCodeId" href="/" class="font-bold text-lg text-white pt-2 px-2 w-full">Content Library</a>
                <a v-if="loggedIn && profile && !hasRole('Anonymous')" href="/profile" class="font-bold text-lg text-white pt-2 px-2 w-full">View Profile</a>
                <a v-if="loggedIn && profile && !hasRole('Anonymous')" href="/account/logout" class="font-bold text-lg text-white pt-2 px-2 w-full">Logout</a>
                <div v-if="!orgSettings.accountsDisabled">
                    <a v-if="!loggedIn || !profile || hasRole('Anonymous')" href="/promo" class="font-bold text-lg text-white pt-2 px-2 w-full">Redeem Promo</a>
                    <a v-if="!loggedIn || !profile || hasRole('Anonymous')" :href="loginUrl" class="font-bold text-lg text-white pt-2 px-2 w-full">Login</a>
                    <a v-if="!loggedIn || !profile || hasRole('Anonymous')" href="/register" class="font-bold text-lg text-white pt-2 px-2 w-full">Create Account</a>
               </div>
            </div>
        </nav>

        <!-- Impersonate Modal -->
        <impersonate-modal
            :active="impersonateModalActive"
            @close="closeImpersonateModal"
        />
    </div>
</template>

<script>
    import { useAuthStore } from '@stores/auth';
    import axios from 'axios';
    import debounce from 'lodash/debounce';
    import ImpersonateModal from "./ImpersonateModal.vue";

    export default {
      components: {
        ImpersonateModal
      },
        parent: true,
        data() {
            return {
                expanded: false,
                searchQuery: '',
                searchResults: [],
                impersonateModalActive: false,
                isImpersonating: false,
                authStore: null,
            };
        },
      props: {
            orgSettings: {
                type: Object,
                default: () => null,
            },
        },
      computed: {
          // Use Pinia auth store's properties - Vue 3 compliant
          profile() {
            return this.authStore?.profile || {};
          },
          loggedIn() {
            return this.authStore?.loggedIn || false;
          },
          userRoles() {
            return this.authStore?.roles || [];
          },
          loginUrl() {
            const currentPath = window.location.pathname || '';
            const normalizedPath = currentPath.toLowerCase();
            const excludedPaths = ['/', '/login', '/register', '/error'];
            if (normalizedPath && !excludedPaths.includes(normalizedPath)) {
              const currentUrl = window.location.pathname + window.location.search + window.location.hash;
              return `/login?returnUrl=${encodeURIComponent(currentUrl)}`;
            }
            return '/login';
          },
          groupedResults() {
              // Group search results by type
              if (!this.searchResults || this.searchResults.length === 0) {
                  return {};
              }
              return this.searchResults.reduce((groups, result) => {
                  if (!groups[result.type]) {
                      groups[result.type] = [];
                  }
                  groups[result.type].push(result);
                  return groups;
              }, {});
          },
      },
      mounted() {
          // Initialize the auth store once and store the reference
          this.authStore = useAuthStore();
          this.checkIsImpersonating();
          if (this.loggedIn) {
              this.startAuthCheck();
          }
      },
        methods: {
            hasRole(role) {
                return this.authStore?.hasRole(role) || false;
            },
            hasAnyClaim(claims) {
                return this.authStore?.hasAnyClaim(claims) || false;
            },
            startAuthCheck() {
                setInterval(async () => {
                    try {
                        await axios.get('/api/auth/check');
                    } catch (error) {
                        // Axios interceptor will handle 401
                    }
                }, 60000);
            },
            userHasAccess(item) {
                if (item.claims) {
                    return this.hasAnyClaim(item.claims);
                }
                return true;
            },
            performSearch() {
                // Only perform the search if the search query is 3 or more characters
                if (this.searchQuery.trim().length < 3) {
                    this.searchResults = []; // Clear results if the input is less than 3 characters
                    return;
                }

                axios
                    .get(`/api/search?query=${encodeURIComponent(this.searchQuery)}`)
                    .then((response) => {
                        this.searchResults = response.data;
                    })
                    .catch((error) => {
                        console.error('Error performing search:', error);
                    });
            },
            // Debounce the performSearch method
            debouncedSearch: debounce(function () {
                this.performSearch();
            }, 300),
            truncateDescription(description) {
                return description.length > 300 ? description.substring(0, 300) + '...' : description;
            },
            navigateTo(url) {
                window.location.href = url;
            },
            closeSearch() {
                this.searchResults = [];
                this.searchQuery = '';
            },
            openImpersonateModal() {
                this.impersonateModalActive = true;
            },
            
            closeImpersonateModal() {
                this.impersonateModalActive = false;
            },
            stopImpersonation() {
              fetch('/Account/StopImpersonation', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
              })
                  .then(response => {
                    if (response.ok) {
                      window.location.href = '/';
                    } else {
                      console.error('Failed to stop impersonating user');
                    }
                  })
                  .catch(error => console.error('Error:', error));
              
              this.checkIsImpersonating();
            },
            checkIsImpersonating() {
              if (!this.loggedIn) return;

              fetch('/Account/IsImpersonating')
                  .then(response => {
                    if (!response.ok) return null;
                    return response.json();
                  })
                  .then(data => {
                    if (data) this.isImpersonating = data.isImpersonating;
                  })
                  .catch(error => console.error('Error:', error));
            }
        },
    };
</script>

<style>
    .search-results {
        position: absolute;
        top: 80px; /* Adjust based on your navbar height */
        left: 50%;
        transform: translateX(-50%);
        background-color: white;
        border: 1px solid black;
        max-height: 400px;
        width: 80%;
        overflow-y: auto;
        z-index: 1000; /* Ensure it appears above other content */
        padding: 20px;
        border-radius: 8px;
    }

    .close-btn {
        position: absolute;
        top: 10px;
        right: 10px;
        background: #122539;
        color: white;
        border: none;
        padding: 5px 10px;
        cursor: pointer;
        font-size: 14px;
    }

    .result-info {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }

    ul > li {
        transition: background-color 0.3s;
    }

        ul > li:hover {
            background-color: #e2e8f0; /* Hover effect for better interaction feedback */
        }
</style>
